import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, TextField, Button, IconButton, Checkbox, Chip, CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { extractActionItems } from '../utils/meetingNotesParser';
import { postTask } from '../api/taskApi';
import { tokenStorage } from '../app/tokenStorage';
import { showSuccess, showError } from '../utils/toast';
import { useSelector } from 'react-redux';
import { useThemeMode } from '../theme/ThemeContext';

/**
 * Paste meeting notes/transcript → extract action items → bulk create tasks.
 *
 * Listens to: 'openMeetingNotesExtractor' event
 * Dispatches: 'taskCreated' after bulk create (UserHomepage refreshes)
 */
const MeetingNotesDialog = () => {
    const [open, setOpen] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [items, setItems] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [creating, setCreating] = useState(false);
    const { themeMode } = useThemeMode();
    const isDark = themeMode === 'dark';
    const selectedOrganisation = useSelector((state) => state.organisation?.selectedOrganisation);

    useEffect(() => {
        const handler = () => {
            setTranscript('');
            setItems([]);
            setSelected(new Set());
            setOpen(true);
        };
        window.addEventListener('openMeetingNotesExtractor', handler);
        return () => window.removeEventListener('openMeetingNotesExtractor', handler);
    }, []);

    const handleExtract = () => {
        const extracted = extractActionItems(transcript);
        setItems(extracted);
        // Select all by default
        setSelected(new Set(extracted.map((_, idx) => idx)));
    };

    const toggleSelected = (idx) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx);
            else next.add(idx);
            return next;
        });
    };

    const handleCreate = async () => {
        const orgId = selectedOrganisation?.id || selectedOrganisation?.organisation_id || selectedOrganisation?.ID;
        const userId = tokenStorage.getUserId();
        if (!orgId || !userId) {
            showError('Missing organisation or user');
            return;
        }

        const toCreate = items.filter((_, idx) => selected.has(idx));
        if (toCreate.length === 0) {
            showError('Select at least one action item');
            return;
        }

        setCreating(true);
        let ok = 0;
        let fail = 0;
        for (const item of toCreate) {
            try {
                await postTask({
                    title: item.title,
                    description: item.mentions.length ? `From meeting notes. Mentions: ${item.mentions.map((m) => `@${m}`).join(', ')}` : 'From meeting notes',
                    userID: Number(userId),
                    responsible: Number(userId),
                    organisation: orgId,
                    service_provider: 59,
                    types: [],
                    status: 16,
                    notification_data: { id: 11, custom_notification_date: null, custom_notification_time: null },
                    accomplished: 0,
                    priority: 1,
                    estimated: 0,
                    projectTimeSpent: { hours: 0, minutes: 0 },
                    recursive: null,
                    permissions: null,
                    project_type: 1
                });
                ok++;
            } catch (err) {
                fail++;
            }
        }
        setCreating(false);
        if (ok > 0) {
            showSuccess(`${ok} task${ok > 1 ? 's' : ''} created${fail > 0 ? `, ${fail} failed` : ''}`);
            window.dispatchEvent(new CustomEvent('taskCreated'));
            setOpen(false);
        } else {
            showError('Failed to create tasks');
        }
    };

    const confidenceColor = (c) => ({
        high: { bg: '#e8f5e9', text: '#1f8f6d' },
        medium: { bg: '#fff8e1', text: '#ef6c00' },
        low: { bg: '#f5f5f5', text: '#757575' }
    }[c] || { bg: '#f5f5f5', text: '#757575' });

    const selectedCount = selected.size;

    return (
        <Dialog
            open={open}
            onClose={() => !creating && setOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px', background: isDark ? '#1e1e1e' : '#fff' } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoAwesomeIcon sx={{ color: '#27ae86' }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '16px', color: isDark ? '#e0e0e0' : '#1f2d3d' }}>
                        Extract Tasks from Notes
                    </Typography>
                </Box>
                <IconButton size="small" onClick={() => setOpen(false)} disabled={creating} sx={{ color: isDark ? '#ccc' : '#666' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                <Typography variant="body2" sx={{ color: isDark ? '#aaa' : '#666', fontSize: '13px', mb: 1.5 }}>
                    Paste meeting notes, Slack messages, or any text. We'll find action items automatically.
                </Typography>
                <TextField
                    multiline
                    fullWidth
                    minRows={5}
                    maxRows={10}
                    value={transcript}
                    onChange={(e) => setTranscript(e.target.value)}
                    placeholder="Paste transcript here...&#10;&#10;Examples:&#10;TODO: Send report to @john&#10;- [ ] Review PR #123&#10;Action: Schedule kickoff meeting"
                    sx={{ '& .MuiInputBase-input': { fontSize: '13px', fontFamily: 'monospace' } }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleExtract}
                        disabled={!transcript.trim()}
                        startIcon={<AutoAwesomeIcon />}
                        sx={{ textTransform: 'none', borderColor: '#27ae86', color: '#27ae86', '&:hover': { borderColor: '#1f8f6d', backgroundColor: 'rgba(39,174,134,0.05)' } }}
                    >
                        Extract action items
                    </Button>
                </Box>

                {items.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? '#ccc' : '#555', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>
                            Found {items.length} action item{items.length > 1 ? 's' : ''}
                        </Typography>
                        <Box sx={{ maxHeight: 240, overflow: 'auto', mt: 1, border: `1px solid ${isDark ? '#333' : '#eee'}`, borderRadius: '8px' }}>
                            {items.map((item, idx) => {
                                const color = confidenceColor(item.confidence);
                                return (
                                    <Box
                                        key={idx}
                                        onClick={() => toggleSelected(idx)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1,
                                            p: 1,
                                            borderBottom: idx < items.length - 1 ? `1px solid ${isDark ? '#2a2a2a' : '#f0f0f0'}` : 'none',
                                            cursor: 'pointer',
                                            '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }
                                        }}
                                    >
                                        <Checkbox checked={selected.has(idx)} size="small" sx={{ p: 0.25, color: '#27ae86', '&.Mui-checked': { color: '#27ae86' } }} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: '13px', color: isDark ? '#e0e0e0' : '#1f2d3d', wordBreak: 'break-word' }}>
                                                {item.title}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                                <Chip
                                                    label={item.confidence}
                                                    size="small"
                                                    sx={{ height: 18, fontSize: '10px', fontWeight: 600, backgroundColor: color.bg, color: color.text }}
                                                />
                                                {item.mentions.map((m) => (
                                                    <Chip
                                                        key={m}
                                                        label={`@${m}`}
                                                        size="small"
                                                        sx={{ height: 18, fontSize: '10px', backgroundColor: isDark ? '#2a2a2a' : '#f0f0f0', color: isDark ? '#bbb' : '#666' }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setOpen(false)} disabled={creating} sx={{ textTransform: 'none', color: isDark ? '#aaa' : '#666' }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleCreate}
                    variant="contained"
                    disabled={creating || selectedCount === 0}
                    startIcon={creating ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
                    sx={{ textTransform: 'none', backgroundColor: '#27ae86', '&:hover': { backgroundColor: '#1f8f6d' } }}
                >
                    {creating ? 'Creating...' : `Create ${selectedCount} task${selectedCount !== 1 ? 's' : ''}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MeetingNotesDialog;
